import ButtonBase from '@mui/material/ButtonBase'
import { styled, type Theme } from '@mui/material/styles'

import { IconButton } from '../icon-button'

import type { FilterItemSelectedColor, FilterItemSize } from './FilterItem'

type ChipStyleProps = { $size: FilterItemSize; active?: boolean }

const chipShouldForwardProp = (prop: PropertyKey) =>
  prop !== '$size' && prop !== 'active'

const smallSizeStyles = (theme: Theme) => ({
  padding: theme.spacing(0.5, 1),
  border: `${theme.border.sm} solid ${theme.palette.border['brand-default']}`,
  borderRadius: theme.radius.md,
  backgroundColor: theme.palette.background.surface,
  height: 'auto',

  '& svg': {
    fontSize: '1rem',
  },

  '&.Mui-disabled': {
    borderColor: theme.palette.border.disabled,
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.background.disabled,
  },
})

// Shared by the plain chip and the clearable one, so the two never drift.
const chipStyles = (theme: Theme, { $size, active }: ChipStyleProps) => ({
  padding: theme.spacing(2, 1),
  border: `1px solid ${theme.palette.border.subtle}`,
  borderRadius: '16px',
  height: 32,
  display: 'flex',
  gap: theme.spacing(0.5),
  whiteSpace: 'nowrap' as const,
  flexShrink: 0,

  '&:focus': {
    border: `1px solid ${theme.palette.border['brand-strong']}`,
    backgroundColor: theme.palette.action['bg-hover'],
  },

  '&:hover': {
    border: `1px solid ${theme.palette.border['brand-strong']}`,
  },

  '&.Mui-disabled': {
    borderColor: theme.palette.border.disabled,
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.background.disabled,
  },

  ...($size === 'small' && smallSizeStyles(theme)),

  ...(active && {
    border: `1px solid ${theme.palette.border['brand-default']}`,
    backgroundColor: theme.palette.action['bg-selected'],

    '&:focus': {
      border: `1px solid ${theme.palette.border['brand-strong']}`,
      backgroundColor: theme.palette.action['bg-selected'],
    },
  }),
})

export const StyledButtonBase = styled(ButtonBase, {
  shouldForwardProp: chipShouldForwardProp,
})<ChipStyleProps>(({ theme, ...props }) => chipStyles(theme, props))

// The clear button cannot sit inside the open button (nested buttons), so the
// shape moves to a wrapper holding both.
export const StyledClearableChip = styled('div', {
  shouldForwardProp: chipShouldForwardProp,
})<ChipStyleProps>(({ theme, ...props }) => ({
  ...chipStyles(theme, props),
  alignItems: 'center',

  '&:focus-within': {
    border: `1px solid ${theme.palette.border['brand-strong']}`,
    backgroundColor: props.active
      ? theme.palette.action['bg-selected']
      : theme.palette.action['bg-hover'],
  },
}))

export const StyledOpenButton = styled(ButtonBase)(({ theme }) => ({
  gap: theme.spacing(0.5),
}))

export const StyledClearButton = styled(IconButton)(({ theme }) => ({
  width: 24,
  height: 24,
  padding: theme.spacing(0.25),
}))

// `$color` is optional: `OffersFilterItem` (front-legacy) imports this badge
// directly and must keep the brand one.
export const Badge = styled('div', {
  shouldForwardProp: (prop) => prop !== '$color',
})<{ $color?: FilterItemSelectedColor }>(({ theme, $color = 'brand' }) => ({
  display: 'flex',
  borderRadius: '16px',
  backgroundColor:
    $color === 'warning'
      ? theme.palette.tag.yellow.background
      : theme.palette.background['brand-strong'],
  padding: theme.spacing(0, 0.5),
  width: '20px',
  height: '20px',
  alignItems: 'center',
  justifyContent: 'center',
  color:
    $color === 'warning'
      ? theme.palette.text.primary
      : theme.palette.text['on-color'],
}))
